// The "Hike lists" map overlay: every hike in the user's hike lists drawn as a
// line, one colour per list, labelled "<list name> – <hike name>".
//
// The lines come from the Martin vector-tile source `get_user_hike_list_hikes`
// behind the Rails /vector proxy (db/functions/get_user_hike_list_hikes_v1.sql),
// the same way My hikes is served. Each tile feature is one (hike, list) pair
// carrying `hike_list_id`, so which lists show, their colours and which list is
// drawn on top are all decided here, as Mapbox expressions — switching a list or
// restyling it never needs new tiles.
//
// Everything here is pure (plus thin localStorage wrappers) so Jest can cover
// those rules — selection, colours, stacking, how per-list styles ride the
// shared `User.routeStyleSettings` column — without a map, Vuex or GraphQL.
//
// Everything is kept in localStorage and synced to the user's account, so it
// follows them across web, iOS and Android:
//   * on/off and which lists are shown  → `User.hikeListOverlaySettings`
//     (the whole state, replaced on every write; null until first synced)
//   * each list's line style            → `routeStyleSettings` under
//     `overlay_hike_list_<id>`. iOS/Android map cloud keys through a fixed
//     table and skip unknown ones, so these keys are invisible to shipped
//     app builds.

export const HIKE_LIST_OVERLAY_SOURCE_ID = 'hike_list_overlay';
export const HIKE_LIST_OVERLAY_ACTIVE_KEY = 'hikeListsOverlayActive';
export const HIKE_LIST_OVERLAY_SELECTION_KEY = 'hikeListOverlaySelectedIds';
export const HIKE_LIST_OVERLAY_STYLES_KEY = 'hiiker_hike_list_overlay_styles';
export const HIKE_LIST_CLOUD_PREFIX = 'overlay_hike_list_';

/**
 * Default list colours, picked by list id (see `defaultHikeListColor`). Taken from the shared line
 * palette (`LINE_STYLE_COLOR_OPTIONS`) minus every colour another overlay
 * defaults to — red (HiiKER hikes), orange (My hikes), blue (Completed); pink
 * (Recordings) isn't in the shared palette — so a list never starts out
 * looking like one of those. The test reads `DEFAULT_OVERLAY_LINE_STYLES`, so
 * changing an overlay's default fails it until this list is updated too.
 */
export const HIKE_LIST_COLOR_PALETTE = [
  '#9E00FF', '#FF00FF', '#FF6F61', '#00FFFF', '#00FF8C', '#FDDF00', '#22242A',
];

export interface OverlayHikeList {
  id: string;
  name: string;
  hikesCount: number;
}

export interface HikeListLinePrefs {
  color: string;
  thickness: number; // multiplier: 0.5 | 1 | 2 | 3
  opacity: number;   // percent, 10-100
}

export type HikeListStyles = Record<string, HikeListLinePrefs>;

// ─────────────────────────── visibility ───────────────────────────

/**
 * Which lists the overlay shows.
 *
 * `null` means the user hasn't picked any: every list shows, including lists
 * they make later. Once they switch a list on or off it becomes the explicit
 * set of shown list ids, so a list made after that stays off until they turn
 * it on. "Show all" goes back to `null`; "Hide all" is an empty selection.
 */
export type HikeListSelection = string[] | null;

export function isHikeListVisible(selection: HikeListSelection, listId: string): boolean {
  return selection === null || selection.includes(String(listId));
}

/** Switches one list on/off, turning "no selection" into an explicit one first. */
export function withHikeListVisibility(
  selection: HikeListSelection,
  allListIds: string[],
  listId: string,
  visible: boolean,
): string[] {
  const id = String(listId);
  const rest = (selection ?? allListIds.map(String)).filter((x) => x !== id);
  return visible ? [...rest, id] : rest;
}

/** A list with no hikes has nothing to draw, so the picker disables it. */
export function isHikeListSelectable(list: Pick<OverlayHikeList, 'hikesCount'>): boolean {
  return (list.hikesCount ?? 0) > 0;
}

export function selectableHikeListCount(lists: OverlayHikeList[]): number {
  return lists.filter(isHikeListSelectable).length;
}

/** Lists actually drawing: switched on and with at least one hike. */
export function visibleHikeListCount(lists: OverlayHikeList[], selection: HikeListSelection): number {
  return lists.filter((l) => isHikeListSelectable(l) && isHikeListVisible(selection, l.id)).length;
}

// ─────────────────────────── colours ───────────────────────────

/**
 * A list's default colour, worked out from its id so web, iOS and Android all
 * pick the same one without storing or syncing anything — no race with the
 * account hydrate. Integer ids → `id mod 7`; anything else → sum of its UTF-16
 * code units mod 7. Keep in lock-step with the iOS/Android implementations
 * (same palette order, same rule — the test fixtures are shared).
 */
export function defaultHikeListColor(listId: string): string {
  const id = String(listId).trim();
  const n = HIKE_LIST_COLOR_PALETTE.length;
  let index: number;
  if (/^-?\d+$/.test(id)) {
    index = ((parseInt(id, 10) % n) + n) % n;
  } else {
    let sum = 0;
    for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i);
    index = sum % n;
  }
  return HIKE_LIST_COLOR_PALETTE[index];
}

export function defaultHikeListPrefs(listId: string): HikeListLinePrefs {
  return { color: defaultHikeListColor(listId), thickness: 1, opacity: 100 };
}

/**
 * "Reset to default" for one list: its id-based colour, 1×, 100%. Stored
 * explicitly (not deleted) because the account merges styles per key, so a
 * deleted key would bring the old custom style back on the next hydrate.
 */
export function resetHikeListStyle(styles: HikeListStyles, listId: string): HikeListStyles {
  return { ...styles, [String(listId)]: defaultHikeListPrefs(listId) };
}

/** The list's own style if the user set one, otherwise its default. */
export function hikeListStyleFor(styles: HikeListStyles, listId: string): HikeListLinePrefs {
  return styles[String(listId)] ?? defaultHikeListPrefs(listId);
}

// ─────────────────────────── map data ───────────────────────────

/** The Martin function source; also the Rails `/vector/<source>` path segment. */
export const HIKE_LIST_TILE_SOURCE = 'get_user_hike_list_hikes';
/** Same floor My hikes has: the /vector proxy refuses lower zooms. */
export const HIKE_LIST_TILE_MIN_ZOOM = 7;
export const HIKE_LIST_TILE_MAX_ZOOM = 14;

/**
 * Tile URL template for the overlay. `cacheBuster` (`t`) defeats Mapbox's own
 * tile cache when the lists change; the Rails tile cache ignores it and is
 * invalidated server-side instead.
 */
export function hikeListTileUrl(origin: string, userId: string | number, cacheBuster: number): string {
  return `${origin}/vector/${HIKE_LIST_TILE_SOURCE}/{z}/{x}/{y}?public_filter=false&user_id=${userId}&t=${cacheBuster}`;
}

/**
 * What the drawn tiles depend on, server-side: which lists exist, their names
 * and how many hikes they hold. When this changes the tiles are re-requested;
 * selection and styles never change it (they are applied on the device).
 */
export function hikeListTileSignature(lists: OverlayHikeList[]): string {
  return lists.map((l) => `${l.id}:${l.hikesCount}:${l.name}`).join('|');
}

export function hikeListLabel(listName: string, hikeName: string): string {
  return `${listName} – ${hikeName}`;
}

/**
 * The title a hike card shows: "<list> – <hike>" when it was opened from a
 * hike-list line (same text as the line's label), otherwise just the hike name.
 */
export function hikeCardTitle(hikeName: string, hikeListName?: string | null): string {
  return hikeListName ? hikeListLabel(hikeListName, hikeName) : hikeName;
}

/**
 * The list name the hike card keeps after something is opened in it: the list's
 * name when a hike was opened from a hike-list line, otherwise none — so it never
 * sticks to the next hike, recording, plan or trail shown in the same card.
 */
export function hikeCardListName(
  opened: 'hike' | 'recording' | 'plannedTrail' | 'trail' | 'cleared',
  opts: { hikeListName?: string | null } = {},
): string {
  return opened === 'hike' ? opts.hikeListName || '' : '';
}

/** Same darkening as the other overlays' halos (BaseMapDialogHelpers.darkenColor). */
export function darkenHex(hex: string, factor = 0.7): string {
  return '#' + (hex.replace('#', '').match(/\w\w/g) || [])
    .map((c) => Math.max(0, Math.min(255, Math.floor(parseInt(c, 16) * factor))).toString(16).padStart(2, '0'))
    .join('');
}

/** A tile feature's list id, as a string (MVT carries it as a number). */
const LIST_ID: any[] = ['to-string', ['get', 'hike_list_id']];

/** `['match', LIST_ID, id1, v1, id2, v2, …, fallback]` — or just `fallback` when there are no lists. */
function matchByList(lists: OverlayHikeList[], value: (list: OverlayHikeList, index: number) => any, fallback: any): any {
  if (lists.length === 0) return fallback;
  const expr: any[] = ['match', LIST_ID];
  lists.forEach((list, index) => { expr.push(String(list.id), value(list, index)); });
  expr.push(fallback);
  return expr;
}

export interface HikeListLayerStyle {
  /** Only lines of shown lists (with hikes) are drawn. */
  filter: any[];
  lineColor: any;
  haloColor: any;
  lineWidth: any[];
  haloWidth: any;
  lineOpacity: any;
  /** Higher lists (earlier in `lists`) are drawn on top — a hike in several lists shows its highest list's colour. */
  lineSortKey: any;
  /** …and their labels win placement. */
  symbolSortKey: any;
}

/**
 * Mapbox paint/layout/filter values for the overlay's line, halo and label
 * layers. `lists` is in picker order (highest first), which decides stacking.
 */
export function hikeListLayerStyle(
  lists: OverlayHikeList[],
  selection: HikeListSelection,
  styles: HikeListStyles,
): HikeListLayerStyle {
  const shownIds = lists
    .filter((l) => isHikeListSelectable(l) && isHikeListVisible(selection, l.id))
    .map((l) => String(l.id));
  const prefs = (list: OverlayHikeList) => hikeListStyleFor(styles, list.id);
  const thickness = matchByList(lists, (l) => prefs(l).thickness, 1);
  const count = lists.length;

  return {
    filter: ['in', LIST_ID, ['literal', shownIds]],
    lineColor: matchByList(lists, (l) => prefs(l).color, '#000000'),
    haloColor: matchByList(lists, (l) => darkenHex(prefs(l).color), '#000000'),
    // Same 1 → 4 px zoom ramp as the other personal-trail lines, × the list's multiplier.
    lineWidth: ['interpolate', ['linear'], ['zoom'], 0, ['*', 1, thickness], 14, ['*', 4, thickness]],
    haloWidth: ['*', 6, thickness],
    lineOpacity: matchByList(lists, (l) => Math.max(0, Math.min(1, prefs(l).opacity / 100)), 1),
    lineSortKey: matchByList(lists, (_l, index) => count - index, 0),
    symbolSortKey: matchByList(lists, (_l, index) => index, count),
  };
}

// ─────────────────────────── picker rows ───────────────────────────

export interface HikeListRow {
  id: string;
  name: string;
  hikesCount: number;
  visible: boolean;  // shown as on — never true for a disabled row
  disabled: boolean; // no hikes, so nothing to show or style
  prefs: HikeListLinePrefs;
}

/** The rows the picker shows, filtered by a case-insensitive name search. */
export function hikeListRows(
  lists: OverlayHikeList[],
  selection: HikeListSelection,
  styles: HikeListStyles,
  query = '',
): HikeListRow[] {
  const q = query.trim().toLowerCase();
  return lists
    .filter((l) => !q || l.name.toLowerCase().includes(q))
    .map((l) => {
      const disabled = !isHikeListSelectable(l);
      return {
        id: String(l.id),
        name: l.name,
        hikesCount: l.hikesCount,
        visible: !disabled && isHikeListVisible(selection, l.id),
        disabled,
        prefs: hikeListStyleFor(styles, l.id),
      };
    });
}

// ─────────────────────────── cloud mapping ───────────────────────────

export interface CloudHikeListStyleEntry {
  routeType: string;
  color?: string | null;
  thicknessMultiplier?: number | null;
  opacity?: number | null;
}

export function hikeListCloudKey(listId: string): string {
  return `${HIKE_LIST_CLOUD_PREFIX}${listId}`;
}

/** `overlay_hike_list_<id>` → `<id>`; `null` for every other route_type. */
export function hikeListIdForCloudKey(routeType: string | null | undefined): string | null {
  if (!routeType) return null;
  const key = routeType.toLowerCase();
  if (!key.startsWith(HIKE_LIST_CLOUD_PREFIX)) return null;
  const id = key.slice(HIKE_LIST_CLOUD_PREFIX.length);
  return id.length > 0 ? id : null;
}

/** Merges the server's per-list styles over `current` (server wins per field). */
export function hikeListStylesFromCloud(
  entries: CloudHikeListStyleEntry[] | null | undefined,
  current: HikeListStyles,
): { styles: HikeListStyles; changed: boolean } {
  const next: HikeListStyles = { ...current };
  let changed = false;

  for (const entry of entries ?? []) {
    const id = hikeListIdForCloudKey(entry.routeType);
    if (!id) continue;
    const prefs = { ...(next[id] ?? defaultHikeListPrefs(id)) };
    if (entry.color != null) prefs.color = entry.color;
    if (entry.thicknessMultiplier != null) prefs.thickness = entry.thicknessMultiplier;
    if (entry.opacity != null) prefs.opacity = Math.round(entry.opacity * 100);
    next[id] = prefs;
    changed = true;
  }

  return { styles: next, changed };
}

export function hikeListStylesToCloud(styles: HikeListStyles): CloudHikeListStyleEntry[] {
  return Object.entries(styles).map(([id, prefs]) => ({
    routeType: hikeListCloudKey(id),
    color: prefs.color,
    thicknessMultiplier: prefs.thickness,
    opacity: Math.max(0, Math.min(1, prefs.opacity / 100)),
  }));
}

// ───────────────────── on/off + selection account sync ─────────────────────

export interface HikeListOverlaySettings {
  active: boolean;
  selection: HikeListSelection;
}

/** `User.hikeListOverlaySettings` / the UpdateUser input of the same name. */
export interface CloudHikeListOverlaySettings {
  active?: boolean | null;
  selectedIds?: Array<string | number> | null;
}

/** The account's state, or `null` when it has never been synced (keep local state then). */
export function hikeListOverlaySettingsFromCloud(
  cloud: CloudHikeListOverlaySettings | null | undefined,
): HikeListOverlaySettings | null {
  if (!cloud || typeof cloud.active !== 'boolean') return null;
  const ids = cloud.selectedIds;
  return { active: cloud.active, selection: Array.isArray(ids) ? ids.map(String) : null };
}

export function hikeListOverlaySettingsToCloud(settings: HikeListOverlaySettings) {
  return { active: settings.active, selectedIds: settings.selection };
}

/** Same switch and same set of shown lists (order doesn't matter). */
export function sameHikeListOverlaySettings(a: HikeListOverlaySettings, b: HikeListOverlaySettings): boolean {
  if (a.active !== b.active) return false;
  if (a.selection === null || b.selection === null) return a.selection === b.selection;
  if (a.selection.length !== b.selection.length) return false;
  const shown = new Set(a.selection);
  return b.selection.every((id) => shown.has(id));
}

// ─────────────────────────── localStorage ───────────────────────────

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) ?? fallback : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

export const readHikeListOverlayActive = (): boolean => readJson(HIKE_LIST_OVERLAY_ACTIVE_KEY, false) === true;
export const writeHikeListOverlayActive = (on: boolean): void => writeJson(HIKE_LIST_OVERLAY_ACTIVE_KEY, on);

/** `null` (no selection) when nothing valid is stored. */
export function readHikeListSelection(): HikeListSelection {
  const value = readJson<unknown>(HIKE_LIST_OVERLAY_SELECTION_KEY, null);
  return Array.isArray(value) ? value.map(String) : null;
}
export function writeHikeListSelection(selection: HikeListSelection): void {
  if (selection === null) {
    try { localStorage.removeItem(HIKE_LIST_OVERLAY_SELECTION_KEY); } catch {}
    return;
  }
  writeJson(HIKE_LIST_OVERLAY_SELECTION_KEY, selection);
}

export function readHikeListOverlaySettings(): HikeListOverlaySettings {
  return { active: readHikeListOverlayActive(), selection: readHikeListSelection() };
}
export function writeHikeListOverlaySettings(settings: HikeListOverlaySettings): void {
  writeHikeListOverlayActive(settings.active);
  writeHikeListSelection(settings.selection);
}

export function readHikeListStyles(): HikeListStyles {
  const value = readJson<unknown>(HIKE_LIST_OVERLAY_STYLES_KEY, {});
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as HikeListStyles) : {};
}
export const writeHikeListStyles = (styles: HikeListStyles): void => writeJson(HIKE_LIST_OVERLAY_STYLES_KEY, styles);
